// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { xClassNames } from "@stoneage/XDL/Utilities";
import React, { FC, memo, useCallback, useEffect } from "react";
import { useSwiper } from "swiper/react";
import { useArrowElement } from "@stoneage/App/Components/XngCarousel/hooks";
import {
    ArrowNavigationProps,
    ArrowPosition,
    ArrowStyleOverrides,
    ArrowType,
    CarouselDirection,
} from "@stoneage/App/Components/XngCarousel/models";
import { buildArrowStyle, DEFAULT_ARROW_ICONS } from "@stoneage/App/Components/XngCarousel/styles/arrow.styles";

const getArrowPosition = (arrowType: ArrowType, direction: CarouselDirection = "horizontal"): ArrowPosition => {
    const isVertical = direction === "vertical";
    if (arrowType === "next") {
        return isVertical ? "bottom" : "right";
    }
    return isVertical ? "top" : "left";
};

const ArrowNavigation: FC<ArrowNavigationProps> = memo(
    ({
        arrowType,
        showOnMobile = false,
        showNavigation = false,
        paginationOffset = 0,
        styleOverrides,
        onDisabled,
        onClick,
        className,
    }) => {
        const swiper = useSwiper();
        const direction: CarouselDirection = swiper?.isHorizontal() ? "horizontal" : "vertical";
        const arrowPosition = getArrowPosition(arrowType, direction);

        const { className: arrowClassName, disabled } = useArrowElement({
            swiper,
            arrowType,
            showOnMobile,
        });

        const handleClick = useCallback(
            (event: React.MouseEvent) => {
                if (arrowType === "next") {
                    swiper?.slideNext();
                } else {
                    swiper?.slidePrev();
                }
                onClick?.(event);
            },
            [swiper, arrowType, onClick],
        );

        useEffect(() => {
            onDisabled?.(disabled);
        }, [disabled, onDisabled]);

        if (!swiper) {
            return null;
        }

        const iconProps: ArrowStyleOverrides = {
            ...DEFAULT_ARROW_ICONS[arrowPosition],
            ...styleOverrides,
        };

        const mergedClassName = xClassNames.append(arrowClassName, className);
        return (
            <Clickable
                {...buildArrowStyle({
                    arrowPosition,
                    showOnMobile,
                    showNavigation,
                    paginationOffset,
                    overrides: styleOverrides,
                })}
                className={mergedClassName}
                aria-label={`carousel-${arrowPosition}`}
                data-test-selector={`Xng_Carousel_${arrowPosition}Arrow`}
                onClick={handleClick}
            >
                <ButtonIcon
                    {...iconProps}
                    className={xClassNames.append([`carousel-${arrowPosition}-icon`, disabled && "disabled"])}
                />
            </Clickable>
        );
    },
);

ArrowNavigation.displayName = "ArrowNavigation";

export default ArrowNavigation;
